/* Copyright (c) 2023-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { KeyboardEventArrowKey, KeyboardEventArrowKeyValue } from 'utils/lib/web-events/keyboard-event';
import { SpatialNavigationDirection, NavigationEventDetail, NavigationEvent } from './spatial-navigation-polyfill';

/**
 * Dispatch a navigation event to the event target
 *
 * @param target the event target
 * @param type navigation event type
 * @param dir navigate direction
 * @returns return false if the event's preventDefault method is called, otherwise return true
 */
export function dispatchNavEvent(
    target: HTMLElement,
    type: 'navbeforefocus' | 'navnotarget',
    dir: SpatialNavigationDirection
): boolean {
    const event: NavigationEvent = new CustomEvent<NavigationEventDetail>(type, {
        bubbles: true,
        cancelable: true,
        detail: {
            dir
        }
    });

    return target.dispatchEvent(event);
}

export function getSpatialNavigationDirection(key: KeyboardEventArrowKey): SpatialNavigationDirection | undefined {
    switch (key) {
        case 'ArrowUp':
            return 'up';
        case 'ArrowDown':
            return 'down';
        case 'ArrowLeft':
            return 'left';
        case 'ArrowRight':
            return 'right';
        default:
            throw new Error(`${key} is not a valid navigation key value`);
    }
}

export function getKeyFromDirection(dir: SpatialNavigationDirection): KeyboardEventArrowKey {
    switch (dir) {
        case 'up':
            return KeyboardEventArrowKeyValue.ArrowUp;
        case 'down':
            return KeyboardEventArrowKeyValue.ArrowDown;
        case 'left':
            return KeyboardEventArrowKeyValue.ArrowLeft;
        case 'right':
            return KeyboardEventArrowKeyValue.ArrowRight;
        default:
            throw new Error(`${dir} is not a valid spatial navigation direction`);
    }
}

export function getReverseDir(dir: SpatialNavigationDirection): SpatialNavigationDirection {
    switch (dir) {
        case 'up':
            return 'down';
        case 'down':
            return 'up';
        case 'left':
            return 'right';
        case 'right':
            return 'left';
    }
}

export function isHorizontalDir(dir: SpatialNavigationDirection): boolean {
    return dir === 'left' || dir === 'right';
}

export function isVerticalDir(dir: SpatialNavigationDirection): boolean {
    return dir === 'up' || dir === 'down';
}

export const DefaultSpatialNavigationScrollAmount = 40;
